import { makeAutoObservable } from 'mobx';
import { primitive } from 'serializr';
import { i18n } from 'core/localization';
import { ApplicationState } from 'framework/applicationState';
import { ModelValidator, ValidatableModel } from 'framework/mobx-integration';
import { createModelSchemaWrapper, listOptional } from 'framework/serializr-integration';
import { ADMIN_USER_ID, MIN_PASSWORD_LENGTH } from './constants';

export class User implements ValidatableModel<User> {
	id: string;
	username: string;
	password: string;
	passwordConfirm: string;
	oldPassword: string;
	accountId: string;
	loginAccountId: string;
	userCategoryId: string;
	userCategoryName: string;
	firstName: string;
	lastName: string;
	email: string;
	pictureId: string;
	iconPack: string;
	locale: string;
	timeZone: string;
	dateFormat: string;
	timeFormat: string;
	dateTimeFormat: string;
	teams: string[];
	phoneNumber: string;
	lockUser: boolean;
	changePassword: boolean;
	landingPages: string[];
	hasNotificationPolicy: boolean;
	forceTeamTags: boolean;
	forceUserTags: boolean;
	tags: string[];
	authType: string;
	externallyManaged: boolean;

	isNew = false;

	validator = new ModelValidator(this);

	constructor() {
		makeAutoObservable(this);
		this.initValidation();
	}

	get isAdminUser() {
		return this.id == ADMIN_USER_ID;
	}

	get isCurrentUser() {
		return ApplicationState.userId == this.id;
	}

	initValidation() {
		const required = i18n('Please complete all required fields');
		const passwordLength = i18n(
			'Password must have minimum 6 characters and not be identical to previous password',
		);
		const passwordsIdentical = i18n('The two passwords must be identical');

		this.validator
			.required('username', undefined, i18n('Please, input username'))
			.required('firstName', undefined, i18n('Please, input first name'))
			.required('lastName', undefined, i18n('Please, input last name'))
			.required('email', undefined, i18n('The email address is invalid'))
			.add('email', {
				message: i18n('The email address is invalid'),
				callback: () => !this.email?.trim() || ModelValidator.emailRegex.test(this.email.trim()),
			})
			.required('locale', undefined, i18n('Please, select user language'))
			.required('timeZone', undefined, i18n('Select time zone...'))
			.required('userCategoryId', () => !this.isAdminUser, i18n('Select role...'))
			.required('password', () => this.isNew, passwordLength)
			.add('password', {
				message: passwordLength,
				callback: () => !this.password || this.password.length >= MIN_PASSWORD_LENGTH,
			})
			.add('password', {
				message: i18n('The password must differ from username'),
				callback: () => !this.password || this.password !== this.username,
				reactionExpression: () => [this.password, this.username],
			})
			.required('passwordConfirm', () => this.isNew, required)
			.add('passwordConfirm', {
				message: passwordsIdentical,
				callback: () => {
					const compare = this.isCurrentUser ? !!this.password : !!this.password && !!this.passwordConfirm;
					return !compare || this.password === this.passwordConfirm;
				},
				reactionExpression: () => [this.password, this.passwordConfirm],
			})
			.required('oldPassword', () => this.isCurrentUser && (!!this.password || !!this.passwordConfirm), required);
	}
}

createModelSchemaWrapper(User, {
	id: primitive(),
	username: primitive(),
	password: primitive(),
	passwordConfirm: primitive(),
	oldPassword: primitive(),
	accountId: primitive(),
	loginAccountId: primitive(),
	userCategoryId: primitive(),
	userCategoryName: primitive(),
	firstName: primitive(),
	lastName: primitive(),
	email: primitive(),
	pictureId: primitive(),
	iconPack: primitive(),
	locale: primitive(),
	timeZone: primitive(),
	dateFormat: primitive(),
	timeFormat: primitive(),
	dateTimeFormat: primitive(),
	teams: listOptional(primitive()),
	phoneNumber: primitive(),
	lockUser: primitive(),
	changePassword: primitive(),
	landingPages: listOptional(primitive()),
	hasNotificationPolicy: primitive(),
	forceTeamTags: primitive(),
	forceUserTags: primitive(),
	tags: listOptional(primitive()),
	authType: primitive(),
	externallyManaged: primitive(),
});
